import {useContext, useMemo} from 'react';

import ApartmentIcon from '@mui/icons-material/Apartment';
import LockIcon from '@mui/icons-material/Lock';
import MailIcon from '@mui/icons-material/Mail';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import SchoolIcon from '@mui/icons-material/School';
import VerifiedIcon from '@mui/icons-material/Verified';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TuneIcon from '@mui/icons-material/Tune';
import ExtensionIcon from '@mui/icons-material/Extension';
import FolderIcon from '@mui/icons-material/Folder';
import GridViewIcon from '@mui/icons-material/GridView';
import DescriptionIcon from '@mui/icons-material/Description';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';

import {UserContext} from '-/contexts/User';
import {SettingsContext} from '-/contexts/Settings';
import {hasPermission} from '-/data/utils';
import {hasFeature} from '-/features';
import {listPacks} from '@embertracking/common/formPacks';

export const SETTINGS_GROUPS = [
    {id: 'department', label: 'Department'},
    {id: 'access', label: 'Access & Security'},
    {id: 'operations', label: 'Operations'},
    {id: 'members', label: 'Members'},
    {id: 'platform', label: 'Platform'}
];

// The one source of truth for nav order, panel order, gating, copy and search
// keywords. Panel order === nav order (they used to differ: Files was tab #14
// but its panel was emitted between Qualifications and Emails).
//
// `keywords` isn't redundant with the field index: four sections render zero
// registered setting rows (Files uses management cards, Duty
// Shifts uses dynamic presets, and Release Notes and Legal are read-only) and would
// otherwise be findable only by their title. Keep them to 5-8 words.
//
// `gate` receives {currentUser, hasFeature, hasChangelog} rather than reaching
// for module state so the matrix is unit-testable without mocking features.js.
export const SETTINGS_SECTIONS = [
    {
        id: 'general',
        label: 'General',
        group: 'department',
        icon: ApartmentIcon,
        blurb: 'Department identity, branding, and how it appears on exports.',
        keywords: ['logo', 'branding', 'name', 'acronym', 'pdf', 'export']
    },
    {
        id: 'login',
        label: 'Login',
        group: 'access',
        icon: LockIcon,
        blurb: 'Station sign-in and member sessions.',
        keywords: ['password', 'session', 'sign in']
    },
    {
        id: 'accessoverview',
        label: 'Access Overview',
        group: 'access',
        icon: GridViewIcon,
        // `wide`: a members × 16-areas matrix, not a form column — see SettingsSection.
        wide: true,
        blurb: 'Every member against every feature area. Read-only.',
        keywords: ['permissions', 'audit', 'admin', 'role', 'matrix', 'who can'],
        // Superadmin-only while it settles, not department-admin. The page reads
        // every member's claims including HR and payroll, so the narrower gate is
        // the safe default until we're happy with what it exposes. Widen to
        // `hasPermission(currentUser, 'admin')` to ship it to departments.
        gate: ({currentUser}) => currentUser?.superadmin === true
    },
    {
        id: 'emails',
        label: 'Emails',
        group: 'access',
        icon: MailIcon,
        blurb: 'When task reminder emails go out and which ones are sent.',
        keywords: ['reminder', 'notification', 'digest', 'task']
    },
    {
        id: 'incidents',
        label: 'Incidents',
        group: 'operations',
        icon: LocalFireDepartmentIcon,
        blurb: 'Response hours, statistics, the dispatch report, and the extra fields on an incident.',
        keywords: ['custom fields', 'statistics', 'standby', 'response']
    },
    {
        id: 'incidentforms',
        label: 'Incident Forms',
        group: 'operations',
        icon: DescriptionIcon,
        blurb: 'Official rescue forms, and the department defaults they are filled with.',
        keywords: ['emcr', 'rescue', 'task registration', 'expense', 'reimbursement', 'pdf'],
        // A pack is only offered where its own feature flag is on (#1406).
        gate: ({hasFeature}) => listPacks().some(pack => hasFeature(pack.feature))
    },
    {
        id: 'checks',
        label: 'Checks',
        group: 'operations',
        icon: FactCheckIcon,
        blurb: 'Pre-trip and post-trip approvals, and where their PDFs are sent.',
        keywords: ['pre-trip', 'post-trip', 'approval', 'pdf', 'paper']
    },
    {
        id: 'fuel',
        label: 'Fuel',
        group: 'operations',
        icon: LocalGasStationIcon,
        blurb: 'Low-tank alerting and who hears about it.',
        keywords: ['tank', 'alert', 'threshold', 'recipients'],
        gate: ({currentUser, hasFeature}) => hasFeature('fuel') && hasPermission(currentUser, 'admin')
    },
    {
        id: 'dutyshifts',
        label: 'Duty Shifts',
        group: 'operations',
        icon: EventAvailableIcon,
        blurb: 'Reusable shift windows offered when scheduling duty.',
        keywords: ['preset', 'shift', 'schedule', 'standby', 'night'],
        gate: ({hasFeature}) => hasFeature('dutyShifts')
    },
    {
        // "Training Records" rather than "Training" because Qualifications next
        // door is the training *curriculum*; this is the record you fill in
        // after a practice, and the label matches the nav item it configures.
        id: 'training',
        label: 'Training Records',
        group: 'operations',
        icon: SchoolIcon,
        blurb: 'What a new training record opens pre-filled with.',
        keywords: ['practice', 'default', 'start time', 'end time', 'pre-fill']
    },
    {
        id: 'ohs',
        label: 'OH&S Meetings',
        group: 'operations',
        icon: HealthAndSafetyIcon,
        blurb: 'Who is reminded to hold and record each month\'s OH&S meeting.',
        keywords: ['ohs', 'health', 'safety', 'designee', 'reminder', 'committee'],
        gate: ({hasFeature}) => hasFeature('ohs')
    },
    {
        id: 'qualifications',
        label: 'Qualifications',
        group: 'members',
        icon: VerifiedIcon,
        blurb: 'The training curriculum and how the matrix is presented.',
        keywords: ['jibc', 'matrix', 'level colors', 'driver operator']
    },
    {
        // Driver clearance (#1580). Its own section rather than a card under Qualifications:
        // that section is about the curriculum and how the matrix draws it, while this is an
        // operational programme with its own matrix, approval queue and per-apparatus targets.
        // Gated on the Requirements module, which is where the feature lives.
        id: 'driverclearance',
        label: 'Driver Clearance',
        group: 'members',
        icon: DirectionsCarIcon,
        blurb: 'Supervised driving hours members log toward being cleared to drive each apparatus.',
        keywords: ['driver clearance', 'driving hours', 'target', 'cleared to drive', 'right seat', 'apparatus', 'driver operator'],
        gate: ({hasFeature}) => hasFeature('requirements')
    },
    {
        id: 'payroll',
        label: 'Payroll',
        group: 'members',
        icon: AccountBalanceIcon,
        blurb: 'Report defaults, pay rates, stipends, and how often periods repeat.',
        keywords: ['cadence', 'stipend', 'wono', 'report', 'period', 'rate', 'hourly', 'pay', 'wage', 'duty shift', 'officer'],
        gate: ({hasFeature}) => hasFeature('payroll')
    },
    {
        id: 'features',
        label: 'Features',
        group: 'platform',
        icon: TuneIcon,
        blurb: 'Turn whole areas of the app on or off for your department.',
        keywords: ['toggle', 'enable', 'disable', 'module']
    },
    {
        id: 'integrations',
        label: 'Integrations',
        group: 'platform',
        icon: ExtensionIcon,
        blurb: 'Dispatch providers and other outside services.',
        keywords: ['iamresponding', 'iar', 'whos responding', 'active911', 'dispatch', 'stream']
    },
    {
        id: 'files',
        label: 'Files',
        group: 'platform',
        icon: FolderIcon,
        blurb: 'The categories documents are filed under.',
        keywords: ['category', 'folder', 'document', 'sop', 'sog'],
        gate: ({hasFeature}) => hasFeature('files')
    },
    {
        id: 'dataexport',
        label: 'Data Export',
        group: 'platform',
        // Secondary (below the divider, icon-free) like Release Notes and
        // Legal: it's a once-in-a-while utility, not part of the daily
        // configuration surface.
        secondary: true,
        blurb: 'Download every record and every uploaded file for your department.',
        keywords: ['backup', 'download', 'csv', 'zip', 'archive', 'everything', 'gdpr', 'foi'],
        // Admin only — a full-site export packages every HR record and payroll
        // line in the department, which is a different sensitivity class from
        // anything else in Settings.
        gate: ({currentUser}) => hasPermission(currentUser, 'admin')
    },
    {
        id: 'changelog',
        label: 'Release Notes',
        group: 'platform',
        secondary: true,
        blurb: 'What shipped in each release of Ember Tracking.',
        keywords: ['changelog', 'version', 'release', 'update'],
        gate: ({hasChangelog}) => hasChangelog
    },
    {
        // Superadmin-only platform tools — not a department setting, so
        // it sits below the divider with the other utility links rather than
        // among the tabs an admin configures. The gate is the `superadmin` flag
        // on the user's own doc, the same one Health and impersonation use; no
        // permission set unlocks it.
        id: 'superadmin',
        label: 'Superadmin',
        group: 'platform',
        secondary: true,
        blurb: 'Platform tools for Ember Tracking staff. Only visible to superadmins.',
        keywords: ['superadmin', 'maintenance', 'rebuild', 'stats', 'job', 'rerun', 'internal'],
        gate: ({currentUser}) => currentUser?.superadmin === true
    },
    {
        id: 'legal',
        label: 'Legal',
        group: 'platform',
        secondary: true,
        blurb: 'Privacy policy and terms of use for your department.',
        keywords: ['privacy', 'terms', 'policy', 'agreement']
    }
];

// Filter by the caller-supplied gating context. Pure, so the gating matrix is
// unit-testable without touching features.js's module-level store.
export const visibleSections = ctx => SETTINGS_SECTIONS.filter(section => !section.gate || section.gate(ctx));

// General lives at the bare /settings so the default landing URL stays clean
// (and so existing deep links keep working).
export const sectionPath = id => (id === 'general' ? '/settings' : `/settings/${id}`);

// Test ids predate the section ids and two of them are hyphenated; keep those
// byte-identical rather than renaming selectors the specs already rely on.
const TEST_ID_SLUGS = {dutyshifts: 'duty-shifts', incidentforms: 'incident-forms'};

export const testIdFor = id => `settings-${TEST_ID_SLUGS[id] || id}-tab`;

// Settings drive hasFeature(); depend on the context (not just the function) so
// toggling a feature checkbox re-renders the rail live — the same trick
// useMenuItems.js uses and documents.
export default function useSettingsSections({hasChangelog = false} = {}) {
    const {currentUser} = useContext(UserContext);
    const settings = useContext(SettingsContext);

    return useMemo(
        () => visibleSections({currentUser, hasFeature, hasChangelog}),
        // `settings` is intentional: it isn't referenced directly, but it backs
        // hasFeature(), so the rail recomputes when a feature is toggled.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [currentUser, settings, hasChangelog]
    );
}
